import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const PUB = process.env.PUBLIC_URL;
const imgUrl = (path) => `${PUB}/${path}`;

/* ─── QUESTIONS ─── */
const QUESTIONS = [
  { id: 1, axis: "PI", text: "登山前の準備は？", choices: [
    { label: "ルート・天気・装備を細かく確認する", value: "P", icon: "📋" },
    { label: "最低限だけ確認して現地で判断する", value: "I", icon: "🎲" }]},
  { id: 2, axis: "PI", text: "登山計画の立て方は？", choices: [
    { label: "時間配分や休憩ポイントまで決める", value: "P", icon: "📅" },
    { label: "ざっくり決めて自由に動く", value: "I", icon: "🌀" }]},
  { id: 3, axis: "PI", text: "予定外の分岐やルートがあったら？", choices: [
    { label: "計画通りのルートを優先する", value: "P", icon: "🗺️" },
    { label: "面白そうなら寄り道する", value: "I", icon: "✨" }]},
  { id: 4, axis: "SA", text: "天候が崩れそうなときは？", choices: [
    { label: "少しでも不安があれば引き返す", value: "S", icon: "🔙" },
    { label: "様子を見ながら進む", value: "A", icon: "👀" }]},
  { id: 5, axis: "SA", text: "体力的にきつくなってきたら？", choices: [
    { label: "無理せずペースダウン・撤退も検討", value: "S", icon: "🛑" },
    { label: "もう少し頑張ってみる", value: "A", icon: "💪" }]},
  { id: 6, axis: "SA", text: "予定より時間が押している場合は？", choices: [
    { label: "安全を優先して引き返す", value: "S", icon: "⏰" },
    { label: "状況を見て行けるところまで進む", value: "A", icon: "⚡" }]},
  { id: 7, axis: "GE", text: "登山の一番の楽しみは？", choices: [
    { label: "山頂に到達すること", value: "G", icon: "🏔️" },
    { label: "景色や自然を楽しむこと", value: "E", icon: "🌸" }]},
  { id: 8, axis: "GE", text: "山頂がガスで見えないときは？", choices: [
    { label: "登頂できた事実が大事", value: "G", icon: "🏅" },
    { label: "景色が見えないなら満足度が下がる", value: "E", icon: "😶‍🌫️" }]},
  { id: 9, axis: "GE", text: "登山の計画を立てるときは？", choices: [
    { label: "登頂目標で山を選ぶ", value: "G", icon: "🎯" },
    { label: "景色や雰囲気で山を選ぶ", value: "E", icon: "🔍" }]},
  { id: 10, axis: "LC", text: "登山はどちらが好き？", choices: [
    { label: "一人で自由に登る", value: "L", icon: "🦅" },
    { label: "誰かと一緒に登る", value: "C", icon: "👥" }]},
  { id: 11, axis: "LC", text: "登山中の過ごし方は？", choices: [
    { label: "自分のペースで静かに楽しむ", value: "L", icon: "🎧" },
    { label: "会話や共有を楽しむ", value: "C", icon: "💬" }]},
  { id: 12, axis: "LC", text: "山の思い出の残し方は？", choices: [
    { label: "自分の中でじっくり味わう", value: "L", icon: "📖" },
    { label: "写真やSNSで共有したい", value: "C", icon: "🤳" }]}
];

const STYLE_LABELS = ["", "セーフティ型", "安定型", "チャレンジ型", "攻め型", "超アグレッシブ"];
const STYLE_COLORS = ["", "#2d6a4f", "#52b788", "#f4a261", "#e85d04", "#9d0208"];

const TYPES = {
  PSGL: { name: "完全攻略ストラテジスト", emoji: "🗺️", catch: "計画で山を制するタイプ", shareText: "私の登山タイプは「完全攻略ストラテジスト」でした⛰️\n\n👉計画で山を制するタイプ\n👉天気・装備・ルート全部チェックしてから動く人\n\nたぶん一緒に行くと安心されるやつ笑\n#登山タイプ診断", traits: ["事前準備が徹底している", "リスク管理が得意でデータ重視", "冷静な判断で山を安全に攻略"], common: ["ザックの中身を前日に並べて写真撮りがち", "天気予報アプリ3つ見比べる", "コースタイム表を自作してる"], accidents: ["完璧主義すぎて疲労を見落とす", "計画外の事態でフリーズしがち"], recommend: ["槍ヶ岳", "剱岳（一般道）", "白馬岳"], retreat: "かなり早い ── 安全を最優先に即撤退", style: 1, popularity: 12, compat: { best: "PAGC", bestNote: "計画＋行動力で最強コンビ", neutral: "ISGL", neutralNote: "感覚派とテンポが合えば名コンビ", bad: "IAEC", badNote: "ノリで動かれるとストレスMAX" }, advice: "計画に縛られすぎず、ときには感覚に従う柔軟性も大切に。", image: "images/mountain-type/type-psgl.png", accent: "#2d6a4f", bg: "#d8f3dc" },
  PSGC: { name: "安全登頂リーダー", emoji: "🛡️", catch: "チームを守る頼れる隊長", shareText: "私の登山タイプは「安全登頂リーダー」でした⛰️\n\n👉みんなを守る安心隊長タイプ\n👉「ちょっとでも危ないなら帰ろう」が言える人\n\n登山で一番必要な人かもしれない笑\n#登山タイプ診断", traits: ["仲間の安全を最優先に動ける", "計画通りに動く安定感", "グループ登山の精神的支柱"], common: ["パーティの最後尾で全員見守りがち", "救急セットが妙に充実", "「無理しないで」が口癖"], accidents: ["仲間を気にしすぎて自分の異変に気づかない"], recommend: ["燕岳", "唐松岳", "西穂独標"], retreat: "早い ── 仲間を守るための即決撤退", style: 1, popularity: 8, compat: { best: "PSGL", bestNote: "計画力×統率力で完璧チーム", neutral: "PSEC", neutralNote: "穏やかさが波長合う", bad: "IAGL", badNote: "勝手に突っ走られて胃が痛い" }, advice: "完璧を求めすぎず、仲間のペースに合わせることも楽しんで。", image: "images/mountain-type/type-psgc.png", accent: "#1b4332", bg: "#b7e4c7" },
  PSEL: { name: "静寂の体験求道者", emoji: "🌿", catch: "一人で深く山を味わう探求者", shareText: "私の登山タイプは「静寂の体験求道者」でした⛰️\n\n👉一人で山をじっくり味わうタイプ\n👉景色とか空気とか、静かな時間が好き\n\nこういう登山が一番贅沢かも\n#登山タイプ診断", traits: ["計画的で慎重、かつ繊細な観察眼", "自然との対話を深く重視", "マイペースで没入する集中力"], common: ["登山口で深呼吸する儀式がある", "苔の写真がスマホに大量保存", "下山後の温泉が本番"], accidents: ["景色に夢中で道迷い"], recommend: ["立山", "屋久島", "尾瀬"], retreat: "早め ── 一人なのでリスクに敏感", style: 1, popularity: 6, compat: { best: "ISEC", bestNote: "自然好き同士で会話が止まらない", neutral: "PSGL", neutralNote: "静かに尊重し合える", bad: "PAEC", badNote: "賑やかすぎて疲れる" }, advice: "たまには仲間と歩くと、新しい発見があるかも。", image: "images/mountain-type/type-psel.png", accent: "#40916c", bg: "#d8f3dc" },
  PSEC: { name: "自然愛でる安全隊", emoji: "🌸", catch: "みんなで安全に自然を楽しむ", shareText: "私の登山タイプは「自然愛でる安全隊」でした⛰️\n\n👉みんなでゆるく自然を楽しむタイプ\n👉無理しない、でもちゃんと楽しむ\n\nこのスタイルが一番長く続く気がする笑\n#登山タイプ診断", traits: ["チームワークと自然観察の両立", "安全ファーストで計画的", "仲間と感動を分かち合う"], common: ["山ごはんが豪華", "花の名前をやたら知ってる", "下山時間きっちり守る"], accidents: ["写真撮影に夢中で行動時間オーバー"], recommend: ["御岳山", "高尾山", "霧ヶ峰"], retreat: "早い ── みんなで合意して撤退できる", style: 1, popularity: 14, compat: { best: "PSEL", bestNote: "繊細な感性が共鳴する", neutral: "ISGC", neutralNote: "ゆるさが噛み合う", bad: "IAGC", badNote: "勢いに振り回されて疲弊" }, advice: "もう少し難しいルートに挑戦してみると世界が広がる！", image: "images/mountain-type/type-psec.png", accent: "#52b788", bg: "#d8f3dc" },
  PAGL: { name: "孤独な山頂ハンター", emoji: "🦅", catch: "一人で頂を狙う野心家", shareText: "私の登山タイプは「孤独な山頂ハンター」でした⛰️\n\n👉とにかく山頂を目指すタイプ\n👉「もうちょい行ける」で粘りがち\n\nたぶん撤退が一番苦手な人です笑\n#登山タイプ診断", traits: ["スピードと体力に圧倒的な自信", "単独行の達人、誰にも縛られない", "山頂への執念が人一倍強い"], common: ["コースタイム0.6掛けが基本", "『まだ行ける』って言いがち", "下山後すぐ次の山探してる"], accidents: ["撤退判断が遅れて天候悪化に巻き込まれる", "ヘッデン使う羽目になる"], recommend: ["甲斐駒ヶ岳", "笊ヶ岳", "鷲羽岳"], retreat: "遅め ── 引き返しのタイミングが最大の弱点", style: 4, popularity: 4, compat: { best: "PSGL", bestNote: "計画派にブレーキかけてもらえる", neutral: "ISGL", neutralNote: "ソロ同士で干渉しない", bad: "PSEC", badNote: "ペース合わなさすぎる" }, advice: "撤退ラインを事前に明確に決めておくこと。強さは柔軟性から生まれる。", image: "images/mountain-type/type-pagl.png", accent: "#c1121f", bg: "#ffe0e0" },
  PAGC: { name: "チーム突破型", emoji: "⚡", catch: "仲間と山頂を攻略するリーダー", shareText: "私の登山タイプは「チーム突破型」でした⛰️\n\n👉みんなで山頂を目指したいタイプ\n👉盛り上げながら登るのが好き\n\nたぶん一番うるさいやつ笑\n#登山タイプ診断", traits: ["行動力が高く仲間を鼓舞する", "困難なルートほど燃える", "グループのエンジン役"], common: ["『あと少し！』を3回言う", "登山中の写真は満面の笑み", "下山後の打ち上げが本番"], accidents: ["ノリでバリエーションルート選んで遭遇遭難"], recommend: ["八ヶ岳赤岳", "妙高山", "蝶ヶ岳"], retreat: "遅め ── チームの士気を優先してしまう", style: 3, popularity: 7, compat: { best: "PSGL", bestNote: "計画と勢いで完璧バランス", neutral: "PAEC", neutralNote: "盛り上がり方が似てる", bad: "ISEL", badNote: "テンションが噛み合わない" }, advice: "チームで一番体力のない人のペースが全体のペース。安全は絶対最優先。", image: "images/mountain-type/type-pagc.png", accent: "#e85d04", bg: "#ffe8d6" },
  PAEL: { name: "自由な体験ソロイスト", emoji: "🎒", catch: "計画はするが感性で動く探検家", shareText: "私の登山タイプは「自由な体験ソロイスト」でした⛰️\n\n👉計画しつつも感覚で動くタイプ\n👉面白そうなら寄り道しがち\n\nこういう人が一番楽しんでる説ある笑\n#登山タイプ診断", traits: ["計画と感性のバランスが絶妙", "新しい体験への好奇心が強い", "自由と安全の両立を目指す"], common: ["山小屋で必ず誰かと仲良くなる", "予定にない山頂に行きがち", "テン泊装備が無駄に凝ってる"], accidents: ["寄り道で時間が押してビバーク"], recommend: ["雲ノ平", "黒部五郎岳", "塩見岳"], retreat: "状況次第 ── 場の空気で判断するクセあり", style: 3, popularity: 5, compat: { best: "IAEL", bestNote: "自由人同士で最高の旅", neutral: "PAGL", neutralNote: "ソロ強者として認め合う", bad: "PSGC", badNote: "管理されると窮屈" }, advice: "攻めの姿勢は素晴らしい。エスケープルートは必ず事前に確認しよう。", image: "images/mountain-type/type-pael.png", accent: "#f77f00", bg: "#fff3e0" },
  PAEC: { name: "山の楽しさ全力グループ", emoji: "🎉", catch: "みんなで楽しく山を攻略！", shareText: "私の登山タイプは「山の楽しさ全力グループ」でした⛰️\n\n👉とにかく楽しいが正義タイプ\n👉気づいたら目的変わってるやつ\n\nたぶん登山というよりイベント笑\n#登山タイプ診断", traits: ["楽しむことが何より最優先", "グループの雰囲気を盛り上げる", "少々の無茶も辞さない陽気さ"], common: ["山頂での乾杯が最大のイベント", "コーヒーミル持参してる人いる", "下山後の温泉&ビールが目的"], accidents: ["楽しさ優先で行動時間オーバー", "宴会装備で荷物激重"], recommend: ["乗鞍岳", "日光白根山", "大菩薩嶺"], retreat: "遅め ── 楽しさ優先で判断が甘くなる", style: 3, popularity: 9, compat: { best: "PSEC", bestNote: "楽しさ＋安全で最高の山旅", neutral: "PAGC", neutralNote: "ノリが合う", bad: "PSEL", badNote: "うるさいと言われる" }, advice: "楽しむのは最高！でも帰りの時間と天気予報だけは絶対確認して。", image: "images/mountain-type/type-paec.png", accent: "#f4a261", bg: "#fff3e0" },
  ISGL: { name: "直感慎重な登頂者", emoji: "🧭", catch: "感覚で動くが引き際も知ってる", shareText: "私の登山タイプは「直感慎重な登頂者」でした⛰️\n\n👉感覚で動くけどちゃんと引けるタイプ\n👉なんだかんだ安全に帰る人\n\n実は一番バランスいい説\n#登山タイプ診断", traits: ["直感と慎重さを兼ね備えた経験者", "柔軟な状況判断が得意", "感覚で危険を察知できる"], common: ["天気予報より雲を信じる", "計画書よりGPS派", "気づくと毎週末山にいる"], accidents: ["疲労時に直感が鈍ることあり"], recommend: ["剱岳早月尾根", "笠ヶ岳", "鳳凰三山"], retreat: "比較的早い ── 体が危険を感じ取る", style: 2, popularity: 6, compat: { best: "PSGL", bestNote: "計画派と感覚派の最強コンビ", neutral: "PAGL", neutralNote: "互いに干渉しない名コンビ", bad: "IAEC", badNote: "雑すぎてストレス" }, advice: "疲労時は直感が鈍るので要注意。休息を大切に。", image: "images/mountain-type/type-isgl.png", accent: "#457b9d", bg: "#dff2ff" },
  ISGC: { name: "のんびり安全グループ", emoji: "☕", catch: "ゆっくり仲間と楽しむ山旅派", shareText: "私の登山タイプは「のんびり安全グループ」でした⛰️\n\n👉ゆっくり楽しく登るタイプ\n👉ペース大事、無理しない\n\nこういう登山が一番平和笑\n#登山タイプ診断", traits: ["急がない・焦らないが最大の強み", "仲間との会話と時間を大切に", "マイペースで安全な山旅"], common: ["山頂着が13時とかある", "おやつタイムが頻繁", "ベンチがあれば即休憩"], accidents: ["のんびりしすぎて日没"], recommend: ["谷川岳天神尾根", "蓼科山", "瑞牆山"], retreat: "早い ── なんとなく引き返せる空気感", style: 1, popularity: 11, compat: { best: "PSEC", bestNote: "ゆるさが完全一致", neutral: "ISEL", neutralNote: "ペース尊重し合える", bad: "PAGL", badNote: "速すぎてついていけない" }, advice: "そのペースが実は最強。焦らず楽しむ姿勢は山では正解です。", image: "images/mountain-type/type-isgc.png", accent: "#74b3ce", bg: "#dff2ff" },
  ISEL: { name: "孤独な山の詩人", emoji: "📖", catch: "静かに山と対話する繊細な魂", shareText: "私の登山タイプは「孤独な山の詩人」でした⛰️\n\n👉一人で静かに山と向き合うタイプ\n👉景色とか空気に浸るのが好き\n\nちょっと分かる人には分かるやつ笑\n#登山タイプ診断", traits: ["感受性が豊かで山に詩的な意味を求める", "自分のペースを絶対に守る", "孤独の中に深い充足感を見出す"], common: ["山行後にエモいツイートしがち", "山頂で本を読む", "雲海フェチ"], accidents: ["浸りすぎて時間感覚を失う"], recommend: ["北横岳", "北八ヶ岳", "鳴神山"], retreat: "早め ── 感覚で空気の変化を察知", style: 1, popularity: 5, compat: { best: "ISGC", bestNote: "静寂を共有できる稀有な存在", neutral: "PSEL", neutralNote: "似たもの同士", bad: "IAGC", badNote: "騒がしすぎて無理" }, advice: "一人の時間は大切。でも下山予定時刻の共有だけは絶対忘れずに。", image: "images/mountain-type/type-isel.png", accent: "#6d6875", bg: "#ede0f0" },
  ISEC: { name: "自然派お散歩グループ", emoji: "🌈", catch: "みんなで自然をのんびり満喫", shareText: "私の登山タイプは「自然派お散歩グループ」でした⛰️\n\n👉ゆるく自然を楽しむタイプ\n👉写真とか会話メイン\n\nこれはこれで正解な登山笑\n#登山タイプ診断", traits: ["自然観察が大好きで知識も豊富", "ゆっくりペースで会話を楽しみながら歩く", "仲間と感動を共有することが最高の喜び"], common: ["スマホ写真が花だらけ", "コースタイムは参考程度", "毎回どこかで道間違える"], accidents: ["道に夢中で気づくと別ルート"], recommend: ["金時山", "弥彦山", "鋸山"], retreat: "早い ── みんなで楽しく引き返せる", style: 1, popularity: 13, compat: { best: "ISGC", bestNote: "ゆるふわ仲間最強", neutral: "PSEL", neutralNote: "自然好き同士", bad: "PAGL", badNote: "速すぎる" }, advice: "この楽しみ方が登山の原点。次のステップの山にもぜひ挑戦を！", image: "images/mountain-type/type-isec.png", accent: "#80b918", bg: "#ecf9d8" },
  IAGL: { name: "野生の登頂マシン", emoji: "⚔️", catch: "本能で山頂を目指す危険な存在", shareText: "私の登山タイプは「野生の登頂マシン」でした⛰️\n\n👉本能で突っ込むタイプ\n👉たぶん「なんとかなる」って思ってる\n\nちょっと危ないらしいです笑\n#登山タイプ診断", traits: ["計画なしで突撃する無謀なパワー型", "体力は無尽蔵だが危険察知が苦手", "山での遭難統計に最も名を刻むタイプ"], common: ["『なんとかなる』が口癖", "天気予報見ない", "ヘッデン忘れがち"], accidents: ["装備不足で低体温症リスク", "ルート確認せず迷う", "撤退判断ができない"], recommend: ["まずは高尾山から", "経験者同伴で陣馬山", "里山リハビリ"], retreat: "かなり遅い ── 引き返せないことも", style: 5, popularity: 2, compat: { best: "PSGL", bestNote: "全力で止めてくれる救世主", neutral: "IAGC", neutralNote: "似た者同士で危険倍増", bad: "ISEL", badNote: "繊細な人が泣く" }, advice: "⚠️ あなたが最大の危険因子かも。計画的な登山者と必ず一緒に行動して！", image: "images/mountain-type/type-iagl.png", accent: "#9d0208", bg: "#ffe0e0" },
  IAGC: { name: "突撃チームリーダー", emoji: "🔥", catch: "勢いで仲間を引っ張る破天荒隊長", shareText: "私の登山タイプは「突撃チームリーダー」でした⛰️\n\n👉勢いで引っ張るタイプ\n👉「いけるいける！」って言いがち\n\n周りはちょっと不安かも笑\n#登山タイプ診断", traits: ["エネルギッシュで仲間を巻き込む力がある", "計画より勢いで動くカリスマ", "その熱量が仲間の撤退判断を鈍らせる"], common: ["『いけるいける』連呼", "後輩が心配そうな顔してる", "達成後のドヤ顔がすごい"], accidents: ["パーティ全員を引きずり込む形での遭難"], recommend: ["まず低山から見直し", "経験者と難易度を下げる"], retreat: "遅い ── 引き返しを言いにくい雰囲気に", style: 4, popularity: 3, compat: { best: "PSGL", bestNote: "ブレーキ役として超重要", neutral: "PAGC", neutralNote: "似たノリ", bad: "PSEL", badNote: "完全にすれ違う" }, advice: "そのエネルギーは素晴らしい！でも撤退基準を必ず最初に決めておいて。", image: "images/mountain-type/type-iagc.png", accent: "#dc2f02", bg: "#ffe0e0" },
  IAEL: { name: "風まかせ一人旅", emoji: "🌬️", catch: "どこへ行くかわからない自由人", shareText: "私の登山タイプは「風まかせ一人旅」でした⛰️\n\n👉ノープランで動く自由人\n👉道より感覚を信じるタイプ\n\nこれで何とかしてるのすごい笑\n#登山タイプ診断", traits: ["直感で動き束縛を嫌う孤高の存在", "偶発的な出会いや発見を楽しむ", "地図より感覚を信じる冒険者"], common: ["登山口を間違える", "計画書？提出してない", "気分で山変える"], accidents: ["地図を見ずに道迷い", "下山届出してなくて捜索遅れ"], recommend: ["地図必修＋低山から", "ガイド付きツアー"], retreat: "気分次第 ── これが最大の危険", style: 4, popularity: 3, compat: { best: "ISGL", bestNote: "感覚派同士で響き合う", neutral: "PAEL", neutralNote: "自由人として尊重", bad: "PSGC", badNote: "完全に管理されたくない" }, advice: "自由は最高。でも地図とエスケープルートの把握は生死に関わります。", image: "images/mountain-type/type-iael.png", accent: "#7b2d8b", bg: "#f0e0f8" },
  IAEC: { name: "山の無法者グループ", emoji: "🤪", catch: "計画ゼロ・ノリだけで山へ行く集団", shareText: "私の登山タイプは「山の無法者グループ」でした⛰️\n\n👉ノリだけで登るタイプ\n👉準備？なにそれ美味しいの\n\nさすがにちょっと反省します笑\n#登山タイプ診断", traits: ["その場のノリが全ての判断基準", "準備は最低限（むしろゼロ）", "全員が同じ思考で誰も止めない"], common: ["『山行こうぜ』が朝LINE", "スニーカーで登山", "コンビニおにぎりだけ"], accidents: ["全員初心者で遭難", "装備不足で低体温症", "誰も地図を持ってない"], recommend: ["⚠️ 登山口そばの散策のみ", "経験者必須"], retreat: "できない ── 全員が同じ方向に突っ走る", style: 5, popularity: 2, compat: { best: "PSGL", bestNote: "プロが必須レベル", neutral: "PAEC", neutralNote: "ノリは合う", bad: "PSGC", badNote: "管理派が頭抱える" }, advice: "⚠️ このグループは遭難リスク最高峰。まず装備と計画から始めよう。", image: "images/mountain-type/type-iaec.png", accent: "#6a040f", bg: "#ffe0e0" },
};

const CODE_ORDER = ["PSGL","PSGC","PSEL","PSEC","PAGL","PAGC","PAEL","PAEC","ISGL","ISGC","ISEL","ISEC","IAGL","IAGC","IAEL","IAEC"];

function calcType(answers) {
  const c = { P:0, I:0, S:0, A:0, G:0, E:0, L:0, C:0 };
  answers.forEach(a => { if (a) c[a]++; });
  return (c.P >= c.I ? "P" : "I") + (c.S >= c.A ? "S" : "A") + (c.G >= c.E ? "G" : "E") + (c.L >= c.C ? "L" : "C");
}

/* ─── SHARE IMAGE ─── */
function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
async function generateShareImage(typeCode) {
  const t = TYPES[typeCode]; if (!t) return null;
  const iconImg = await loadImage(imgUrl(`images/mountain-type/icon-${typeCode.toLowerCase()}.png`));
  const canvas = document.createElement("canvas");
  canvas.width = 1080; canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  const grad = ctx.createLinearGradient(0, 0, 1080, 1080);
  grad.addColorStop(0, t.bg); grad.addColorStop(1, "#f0ede6");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, 1080, 1080);
  ctx.fillStyle = t.accent; ctx.fillRect(0, 0, 1080, 12); ctx.fillRect(0, 1068, 1080, 12);
  ctx.fillStyle = t.accent + "22";
  ctx.beginPath(); ctx.moveTo(0, 900);
  ctx.lineTo(200, 600); ctx.lineTo(350, 750); ctx.lineTo(540, 400);
  ctx.lineTo(700, 620); ctx.lineTo(850, 500); ctx.lineTo(1080, 680);
  ctx.lineTo(1080, 1080); ctx.lineTo(0, 1080); ctx.closePath(); ctx.fill();
  if (iconImg) {
    const iconH = 340;
    const iconW = (iconImg.width / iconImg.height) * iconH;
    ctx.drawImage(iconImg, 540 - iconW / 2, 20, iconW, iconH);
  } else {
    ctx.font = "120px serif"; ctx.fillText(t.emoji, 540, 240);
  }
  ctx.textAlign = "center";
  ctx.fillStyle = t.accent;
  roundRect(ctx, 440, 368, 200, 52, 26); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.font = "bold 28px 'Noto Sans JP', sans-serif"; ctx.fillText(typeCode, 540, 403);
  ctx.fillStyle = t.accent; ctx.font = "bold 56px 'Noto Sans JP', sans-serif";
  wrapText(ctx, t.name, 540, 510, 820, 68);
  ctx.fillStyle = "#555"; ctx.font = "36px 'Noto Sans JP', sans-serif"; ctx.fillText(t.catch, 540, 630);
  ctx.fillStyle = "#333"; ctx.font = "bold 26px 'Noto Sans JP', sans-serif";
  ctx.fillText("登山スタイル傾向：" + STYLE_LABELS[t.style], 540, 710);
  ctx.strokeStyle = t.accent + "44"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(140, 750); ctx.lineTo(940, 750); ctx.stroke();
  ctx.font = "bold 30px 'Noto Sans JP', sans-serif"; ctx.fillStyle = t.accent;
  ctx.fillText("👉 こういう人", 540, 810);
  ctx.font = "26px 'Noto Sans JP', sans-serif"; ctx.fillStyle = "#444";
  t.common.slice(0, 2).forEach((tr, i) => ctx.fillText("・" + tr, 540, 858 + i * 48));
  ctx.fillStyle = t.accent; ctx.font = "bold 30px 'Noto Sans JP', sans-serif"; ctx.fillText("#登山タイプ診断", 540, 980);
  ctx.fillStyle = "#888"; ctx.font = "22px 'Noto Sans JP', sans-serif"; ctx.fillText("16タイプで分かるあなたの山スタイル", 540, 1020);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob ? URL.createObjectURL(blob) : canvas.toDataURL("image/png"));
    }, "image/png");
  });
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x+r, y);
  ctx.lineTo(x+w-r, y); ctx.arcTo(x+w, y, x+w, y+r, r);
  ctx.lineTo(x+w, y+h-r); ctx.arcTo(x+w, y+h, x+w-r, y+h, r);
  ctx.lineTo(x+r, y+h); ctx.arcTo(x, y+h, x, y+h-r, r);
  ctx.lineTo(x, y+r); ctx.arcTo(x, y, x+r, y, r); ctx.closePath();
}
function wrapText(ctx, text, cx, y, maxW, lineH) {
  if (ctx.measureText(text).width <= maxW) { ctx.fillText(text, cx, y); return; }
  const mid = Math.floor(text.length / 2);
  ctx.fillText(text.slice(0, mid), cx, y); ctx.fillText(text.slice(mid), cx, y + lineH);
}

/* ─── SUB COMPONENTS ─── */
function Card({ title, accent, children, badge }) {
  return (
    <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginTop: "16px", borderLeft: `4px solid ${accent}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ fontSize: "14px", fontWeight: 700, color: accent }}>{title}</div>
        {badge && <span style={{ fontSize: "11px", background: accent, color: "#fff", padding: "3px 8px", borderRadius: "20px", fontWeight: 700 }}>{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function CompatRow({ label, labelText, type, note, bg, border }) {
  return (
    <div style={{ background: bg, border: `2px solid ${border}33`, borderRadius: "14px", padding: "14px", display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{ background: border, color: "#fff", width: "44px", height: "44px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: 900, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "11px", color: border, fontWeight: 700, marginBottom: "2px" }}>{labelText}</div>
        <div style={{ fontWeight: 700, color: "#333", fontSize: "14px" }}>{type.emoji} {type.name}</div>
        <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>{note}</div>
      </div>
    </div>
  );
}

/* ─── TOP SCREEN ─── */
function TopScreen({ onStart, onShowAll }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(160deg, #1b4332 0%, #2d6a4f 40%, #52b788 100%)", padding: "32px 24px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
        {[{top:"8%",left:"10%",fs:120,o:0.08},{top:"15%",right:"5%",fs:80,o:0.06},{bottom:"20%",left:"5%",fs:100,o:0.07}].map((s,i)=>(
          <div key={i} style={{position:"absolute",...s,fontSize:s.fs+"px",opacity:s.o}}>⛰️</div>
        ))}
      </div>
      <div style={{ textAlign: "center", zIndex: 1, maxWidth: "420px", width: "100%" }}>
        <div style={{ fontSize: "13px", letterSpacing: "3px", color: "#b7e4c7", fontWeight: 700, marginBottom: "12px" }}>MOUNTAIN TYPE TEST</div>
        <h1 style={{ fontSize: "clamp(26px, 7vw, 36px)", fontWeight: 900, color: "#fff", lineHeight: 1.3, marginBottom: "8px", textShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>登山タイプ診断</h1>
        <p style={{ fontSize: "16px", color: "#b7e4c7", marginBottom: "32px", lineHeight: 1.6 }}>16タイプで分かる<br/>あなたの山スタイル</p>
        <div style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "16px", padding: "20px", marginBottom: "24px", textAlign: "left" }}>
          {[{i:"📝",t:"全12問・約3分で完了"},{i:"🎯",t:"4軸×2択で16タイプに分類"},{i:"💚",t:"あるある・相性・おすすめ山も診断"},{i:"📱",t:"結果をSNSでシェアできる"}].map((x,i)=>(
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", color: "#fff", marginBottom: i<3?"10px":0, fontSize: "14px" }}>
              <span style={{ fontSize: "20px" }}>{x.i}</span><span>{x.t}</span>
            </div>
          ))}
        </div>
        <div style={{ width: "100%", borderRadius: "16px", overflow: "hidden", marginBottom: "20px", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
          <img src={imgUrl("images/mountain-type/top-hero.png")} alt="登山タイプ診断" style={{ width: "100%", display: "block", aspectRatio: "16/9", objectFit: "cover" }} />
        </div>
        <button onClick={onStart} style={{ width: "100%", padding: "18px", fontSize: "18px", fontWeight: 900, background: "linear-gradient(135deg, #f4a261, #e76f51)", color: "#fff", border: "none", borderRadius: "50px", cursor: "pointer", boxShadow: "0 8px 24px rgba(231,111,81,0.5)", fontFamily: "inherit", letterSpacing: "1px" }}>診断スタート ⛰️</button>
        <button onClick={onShowAll} style={{ marginTop: "12px", width: "100%", padding: "12px", background: "transparent", color: "rgba(255,255,255,0.85)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "50px", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", fontWeight: 600 }}>16タイプ一覧を見る →</button>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", marginTop: "16px" }}>登山の安全意識向上のためのコンテンツです</p>
      </div>
    </div>
  );
}

/* ─── QUIZ SCREEN ─── */
function QuizScreen({ onFinish }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [selected, setSelected] = useState(null);
  const q = QUESTIONS[current];

  function choose(value) {
    setSelected(value);
    const newAnswers = [...answers]; newAnswers[current] = value; setAnswers(newAnswers);
    setTimeout(() => {
      setSelected(null);
      if (current < QUESTIONS.length - 1) { setCurrent(c => c + 1); }
      else onFinish(newAnswers);
    }, 380);
  }
  function goBack() { if (current > 0) setCurrent(c => c - 1); }

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "linear-gradient(160deg, #f0ede6 0%, #e8f5e9 100%)" }}>
      <div style={{ background: "#1b4332", padding: "16px 20px", display: "flex", alignItems: "center", gap: "12px" }}>
        {current > 0 && <button onClick={goBack} style={{ background: "none", border: "none", color: "#b7e4c7", fontSize: "20px", cursor: "pointer", padding: "4px 8px", flexShrink: 0 }}>←</button>}
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ color: "#b7e4c7", fontSize: "13px" }}>Q{current + 1} / {QUESTIONS.length}</span>
            <span style={{ color: "#b7e4c7", fontSize: "13px" }}>{Math.round((current / QUESTIONS.length) * 100)}%</span>
          </div>
          <div style={{ height: "6px", background: "rgba(255,255,255,0.15)", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: "3px", background: "linear-gradient(90deg, #52b788, #95d5b2)", width: `${(current / QUESTIONS.length) * 100}%`, transition: "width 0.4s ease" }}></div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px" }}>
        <div style={{ width: "100%", maxWidth: "480px" }}>
          <div style={{ background: "#fff", borderRadius: "20px", padding: "32px 24px", boxShadow: "0 8px 32px rgba(27,67,50,0.12)", marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#52b788", letterSpacing: "2px", marginBottom: "12px" }}>QUESTION {current + 1}</div>
            <h2 style={{ fontSize: "clamp(18px, 5vw, 22px)", fontWeight: 700, color: "#1b4332", lineHeight: 1.5 }}>{q.text}</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {q.choices.map((c, i) => (
              <button key={i} onClick={() => choose(c.value)} style={{ background: selected === c.value ? "linear-gradient(135deg, #1b4332, #2d6a4f)" : "#fff", color: selected === c.value ? "#fff" : "#1b4332", border: "2px solid", borderColor: selected === c.value ? "#1b4332" : "#d4e6dc", borderRadius: "16px", padding: "18px 20px", display: "flex", alignItems: "center", gap: "14px", cursor: "pointer", fontFamily: "inherit", fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 600, textAlign: "left", width: "100%", transition: "all 0.2s ease" }}>
                <span style={{ fontSize: "28px", flexShrink: 0 }}>{c.icon}</span>
                <span style={{ lineHeight: 1.4 }}>{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── RESULT SCREEN ─── */
function ResultScreen({ typeCode, onRetry, onShowAll, onUpdateUrl }) {
  const t = TYPES[typeCode];
  const [shareImg, setShareImg] = useState(null);
  const [imgReady, setImgReady] = useState(false);
  const [copied, setCopied] = useState(false);

  const rank = useMemo(() => {
    const sorted = CODE_ORDER.slice().sort((a, b) => TYPES[b].popularity - TYPES[a].popularity);
    return sorted.indexOf(typeCode) + 1;
  }, [typeCode]);

  useEffect(() => {
    onUpdateUrl(typeCode);

    let blobUrl = null;
    const timer = setTimeout(async () => {
      blobUrl = await generateShareImage(typeCode);
      setShareImg(blobUrl);
      setImgReady(true);
    }, 300);
    return () => {
      clearTimeout(timer);
      if (blobUrl && blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
    };
  }, [typeCode, onUpdateUrl]);

  const shareUrl = window.location.href;

  function downloadImage() {
    if (!shareImg) return;
    const a = document.createElement("a");
    a.href = shareImg; a.download = `登山タイプ診断_${typeCode}_${t.name}.png`; a.click();
  }
  function shareTwitter() {
    const txt = (t.shareText || `私の登山タイプは「${t.name}」でした⛰️\n#登山タイプ診断`) + "\n#ヤマノブログ\n\nこれ友達もやってみてほしい👇\n" + shareUrl;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(txt)}`, "_blank");
  }
  function shareLine() {
    const txt = `登山タイプ診断やったら「${t.name}」だった${t.emoji}\nやってみて👇\n${shareUrl}`;
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(txt)}`, "_blank");
  }
  function copyLink() {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const styleColor = STYLE_COLORS[t.style];
  const compatBest = TYPES[t.compat.best], compatNeu = TYPES[t.compat.neutral], compatBad = TYPES[t.compat.bad];

  return (
    <div style={{ background: "#f0ede6", minHeight: "100vh" }}>
      <div style={{ background: `linear-gradient(160deg, ${t.accent} 0%, ${t.accent}cc 100%)`, padding: t.image ? "0" : "40px 24px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        {t.image && (
          <div style={{ position: "relative" }}>
            <img src={imgUrl(t.image)} alt={t.name} style={{ width: "100%", display: "block", aspectRatio: "16/9", objectFit: "cover", opacity: 0.85 }} />
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, transparent 30%, ${t.accent}ee 100%)` }} />
          </div>
        )}
        {!t.image && (
          <svg viewBox="0 0 400 120" style={{ position: "absolute", bottom: 0, left: 0, right: 0, width: "100%", opacity: 0.15 }}>
            <polygon points="0,120 80,40 160,90 240,20 320,70 400,10 400,120" fill="#fff"/>
          </svg>
        )}
        <div style={{ position: "relative", zIndex: 1, padding: t.image ? "24px 24px 32px" : "0" }}>
          <div style={{ display: "inline-block", background: "rgba(255,255,255,0.2)", borderRadius: "50px", padding: "6px 16px", marginBottom: "12px", fontSize: "12px", color: "rgba(255,255,255,0.95)", letterSpacing: "2px", fontWeight: 700 }}>診断結果 ── {typeCode}</div>
          <div style={{ marginBottom: "8px" }}>
            <img src={imgUrl(`images/mountain-type/icon-${typeCode.toLowerCase()}.png`)} alt={t.name} style={{ height: "180px", width: "auto", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))" }} />
          </div>
          <h1 style={{ fontSize: "clamp(24px, 7vw, 36px)", fontWeight: 900, color: "#fff", marginBottom: "8px", textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>{t.name}</h1>
          <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.9)", marginBottom: "20px" }}>{t.catch}</p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(0,0,0,0.25)", borderRadius: "50px", padding: "8px 20px", backdropFilter: "blur(4px)" }}>
            <span style={{ color: "#fff", fontSize: "13px", fontWeight: 700 }}>登山スタイル傾向</span>
            <span style={{ color: "#fff", fontSize: "14px", fontWeight: 900, background: styleColor, borderRadius: "20px", padding: "3px 12px" }}>{STYLE_LABELS[t.style]}</span>
          </div>
          <div style={{ marginTop: "16px", color: "rgba(255,255,255,0.9)", fontSize: "13px" }}>
            👥 全16タイプ中 <strong style={{ fontSize: "15px" }}>第{rank}位</strong>の登場頻度（推定 {t.popularity}%）
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "520px", margin: "0 auto", padding: "0 16px 40px" }}>
        <div style={{ background: "linear-gradient(135deg, #fff8e7, #ffeebb)", borderRadius: "20px", padding: "24px 20px", marginTop: "16px", border: "2px dashed #f4a261", position: "relative" }}>
          <div style={{ position: "absolute", top: "-12px", left: "20px", background: "#f4a261", color: "#fff", padding: "4px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 900 }}>👀 あるある</div>
          <div style={{ fontSize: "13px", color: "#9a6f1a", marginTop: "8px", marginBottom: "14px", fontWeight: 700 }}>このタイプ、こういう人</div>
          {t.common.map((c, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: "12px", padding: "14px 16px", marginBottom: i < t.common.length - 1 ? "8px" : 0, fontSize: "15px", fontWeight: 600, color: "#5a3a08", boxShadow: "0 2px 8px rgba(244,162,97,0.15)" }}>「{c}」</div>
          ))}
          <div style={{ textAlign: "center", marginTop: "14px", fontSize: "13px", color: "#9a6f1a", fontWeight: 700 }}>当たってる？笑</div>
        </div>

        <Card title="🧬 あなたの特徴" accent={t.accent}>
          {t.traits.map((tr, i) => (
            <div key={i} style={{ display: "flex", gap: "10px", marginBottom: i < t.traits.length - 1 ? "10px" : 0 }}>
              <span style={{ color: t.accent, fontWeight: 700, flexShrink: 0 }}>▶</span>
              <span style={{ color: "#333", lineHeight: 1.5 }}>{tr}</span>
            </div>
          ))}
        </Card>

        <Card title="🔙 撤退判断傾向" accent={t.accent}>
          <div style={{ background: t.style >= 4 ? "#fff0f0" : "#f0fff4", border: `1px solid ${t.style >= 4 ? "#ffcccc" : "#b7e4c7"}`, borderRadius: "10px", padding: "14px 16px", color: t.style >= 4 ? "#9d0208" : "#1b4332", fontWeight: 600, lineHeight: 1.5 }}>{t.retreat}</div>
        </Card>

        <Card title="⚠️ このタイプがやりがちな事故" accent={t.accent}>
          {t.accidents.map((a, i) => (
            <div key={i} style={{ background: "#fff5f5", borderLeft: `3px solid ${styleColor}`, borderRadius: "8px", padding: "10px 14px", marginBottom: i < t.accidents.length - 1 ? "8px" : 0, fontSize: "14px", color: "#5a1a1a" }}>・{a}</div>
          ))}
        </Card>

        <Card title="⛰️ あなたにおすすめの山" accent={t.accent}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {t.recommend.map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: t.bg, borderRadius: "10px", fontSize: "14px", fontWeight: 600, color: t.accent }}>
                <span style={{ fontSize: "18px" }}>📍</span>{m}
              </div>
            ))}
          </div>
        </Card>

        <div style={{ marginTop: "20px", marginBottom: "8px", textAlign: "center", fontSize: "13px", fontWeight: 700, color: "#666", letterSpacing: "2px" }}>── 相性診断 ──</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <CompatRow label="◎" labelText="一緒に登ると最高" type={compatBest} note={t.compat.bestNote} bg="#e6f7ed" border="#52b788" />
          <CompatRow label="△" labelText="ペースが合うときも" type={compatNeu} note={t.compat.neutralNote} bg="#fff8e6" border="#f4a261" />
          <CompatRow label="⚠" labelText="ケンカしがち" type={compatBad} note={t.compat.badNote} bg="#fff0f0" border="#e85d04" />
        </div>

        <div style={{ background: "linear-gradient(135deg, #1b4332, #2d6a4f)", borderRadius: "16px", padding: "20px", marginTop: "16px" }}>
          <div style={{ color: "#95d5b2", fontSize: "13px", fontWeight: 700, marginBottom: "8px" }}>💡 ワンポイントアドバイス</div>
          <p style={{ color: "#fff", lineHeight: 1.7, fontSize: "15px" }}>{t.advice}</p>
        </div>

        <div style={{ background: "#fff", borderRadius: "20px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", marginTop: "16px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#1b4332", marginBottom: "8px", textAlign: "center" }}>📣 結果をシェアする</h3>
          {imgReady && shareImg && (
            <div style={{ marginBottom: "16px", position: "relative" }}>
              <img src={shareImg} alt="シェア画像" style={{ width: "100%", borderRadius: "12px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}/>
              <div style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: "11px", padding: "4px 8px", borderRadius: "6px", fontWeight: 700 }}>長押しで保存</div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <button onClick={shareTwitter} style={{ background: "#000", color: "#fff", border: "none", borderRadius: "12px", padding: "14px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <span style={{ fontSize: "18px" }}>𝕏</span> Xでシェア
            </button>
            <button onClick={shareLine} style={{ background: "#06c755", color: "#fff", border: "none", borderRadius: "12px", padding: "14px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              💬 LINEで友達に送る
            </button>
            <button onClick={downloadImage} style={{ background: t.accent, color: "#fff", border: "none", borderRadius: "12px", padding: "14px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              📥 画像を保存（SNS用）
            </button>
            <button onClick={copyLink} style={{ background: copied ? "#52b788" : "#f0ede6", color: copied ? "#fff" : "#1b4332", border: "2px solid", borderColor: copied ? "#52b788" : "#d4e6dc", borderRadius: "12px", padding: "14px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {copied ? "✅ コピーしました！" : "🔗 結果URLをコピー"}
            </button>
          </div>
        </div>

        <button onClick={onShowAll} style={{ width: "100%", marginTop: "16px", background: "#fff", border: `2px solid ${t.accent}`, color: t.accent, borderRadius: "12px", padding: "14px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          🗂️ 16タイプ一覧を見る
        </button>
        <button onClick={onRetry} style={{ width: "100%", marginTop: "10px", background: "none", border: "2px solid #2d6a4f", color: "#2d6a4f", borderRadius: "12px", padding: "14px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          🔄 もう一度診断する
        </button>
        <p style={{ textAlign: "center", color: "#aaa", fontSize: "12px", marginTop: "24px", lineHeight: 1.6 }}>この診断はエンターテイメント目的です。<br/>実際の登山には十分な準備と装備が必要です。</p>
      </div>
    </div>
  );
}

/* ─── ALL TYPES SCREEN ─── */
function AllTypesScreen({ onBack, onPickType }) {
  const sorted = CODE_ORDER.slice().sort((a, b) => TYPES[b].style - TYPES[a].style);
  const dangerRanking = sorted.slice(0, 5);

  return (
    <div style={{ minHeight: "100vh", background: "#f0ede6" }}>
      <div style={{ background: "linear-gradient(160deg, #1b4332, #2d6a4f)", padding: "32px 24px", textAlign: "center", position: "relative" }}>
        <button onClick={onBack} style={{ position: "absolute", top: "16px", left: "16px", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", padding: "8px 14px", borderRadius: "20px", cursor: "pointer", fontSize: "13px", fontFamily: "inherit" }}>← 戻る</button>
        <h1 style={{ color: "#fff", fontSize: "24px", fontWeight: 900, marginBottom: "8px" }}>16タイプ図鑑</h1>
        <p style={{ color: "#b7e4c7", fontSize: "13px" }}>あなたの友達はどれ？</p>
      </div>
      <div style={{ maxWidth: "560px", margin: "0 auto", padding: "20px 16px 40px" }}>
        <div style={{ background: "linear-gradient(135deg, #fff0f0, #ffe0e0)", borderRadius: "16px", padding: "20px", marginBottom: "24px", border: "2px solid #ffcccc" }}>
          <div style={{ fontSize: "16px", fontWeight: 900, color: "#9d0208", marginBottom: "4px" }}>🚨 遭難しやすいタイプランキング</div>
          <div style={{ fontSize: "12px", color: "#5a1a1a", marginBottom: "14px" }}>気をつけて...！</div>
          {dangerRanking.map((code, i) => {
            const tt = TYPES[code];
            const medals = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];
            return (
              <div key={code} onClick={() => onPickType(code)} style={{ cursor: "pointer", background: "#fff", borderRadius: "10px", padding: "10px 14px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                <span style={{ fontSize: "20px" }}>{medals[i]}</span>
                <span style={{ fontSize: "24px" }}>{tt.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "14px", color: "#333" }}>{tt.name}</div>
                  <div style={{ fontSize: "11px", color: "#888" }}>{code} ・ {STYLE_LABELS[tt.style]}</div>
                </div>
                <span style={{ color: "#9d0208", fontSize: "16px" }}>→</span>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: "14px", fontWeight: 700, color: "#1b4332", marginBottom: "12px" }}>📚 全16タイプ</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
          {CODE_ORDER.map(code => {
            const tt = TYPES[code];
            return (
              <div key={code} onClick={() => onPickType(code)} style={{ cursor: "pointer", background: "#fff", borderRadius: "12px", padding: "14px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", borderTop: `3px solid ${tt.accent}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "24px" }}>{tt.emoji}</span>
                  <span style={{ fontSize: "10px", background: tt.accent, color: "#fff", padding: "2px 6px", borderRadius: "10px", fontWeight: 700 }}>{code}</span>
                </div>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#222", lineHeight: 1.3, marginBottom: "4px" }}>{tt.name}</div>
                <div style={{ fontSize: "10px", color: "#888", lineHeight: 1.3 }}>{tt.catch}</div>
                <div style={{ marginTop: "8px", fontSize: "10px", color: STYLE_COLORS[tt.style], fontWeight: 700 }}>● {STYLE_LABELS[tt.style]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── PAGE COMPONENT ─── */
function MountainTypePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [screen, setScreen] = useState("top");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const r = params.get("result");
    if (r && TYPES[r]) { setResult(r); setScreen("result"); }
  }, [location.search]);

  const handleUpdateUrl = React.useCallback((typeCode) => {
    navigate(`/mountain-type?result=${typeCode}`, { replace: true });
  }, [navigate]);

  function handleFinish(answers) {
    const code = calcType(answers);
    setResult(code); setScreen("result"); window.scrollTo(0, 0);
    // クイズ完了時のみカウント
    (async () => {
      try {
        const { doc, setDoc, increment, serverTimestamp } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        await setDoc(
          doc(db, 'mountainTypeStats', code),
          { count: increment(1), updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (_) { /* 集計失敗は無視 */ }
    })();
  }
  function handleRetry() {
    navigate('/mountain-type', { replace: true });
    setResult(null); setScreen("top"); window.scrollTo(0, 0);
  }

  return (
    <div style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      {screen === "top" && <TopScreen onStart={() => setScreen("quiz")} onShowAll={() => setScreen("all")} />}
      {screen === "quiz" && <QuizScreen onFinish={handleFinish} />}
      {screen === "all" && <AllTypesScreen onBack={() => setScreen(result ? "result" : "top")} onPickType={(c) => { setResult(c); setScreen("result"); window.scrollTo(0,0); }} />}
      {screen === "result" && result && <ResultScreen typeCode={result} onRetry={handleRetry} onShowAll={() => setScreen("all")} onUpdateUrl={handleUpdateUrl} />}
    </div>
  );
}

export default MountainTypePage;
